import { normalizePostalAreaCode, postalAreaLookupCodes } from '@/lib/postal-code';
import type { SchedulerTechnician } from '@/types/scheduler';

/**
 * One required skill (a ServiceType) a job type / visit asks for. Any of the
 * identifiers may be present; `id` and `slug` are matched against the
 * technician's expertise, `name` is only for display.
 */
export interface RequiredSkillRef {
  id?: string | null;
  slug?: string | null;
  name?: string | null;
}

/** Keep only refs that can actually be matched (an id or a slug). */
export function normalizeRequiredSkills(
  skills: readonly RequiredSkillRef[] | null | undefined,
): RequiredSkillRef[] {
  return (skills ?? []).filter((skill) => Boolean(skill.id) || Boolean(skill.slug));
}

/** An expertise entry satisfies a skill only while its certification is current. */
function expertiseIsCurrent(expiresAt: string | null | undefined, now: number): boolean {
  return expiresAt == null || new Date(expiresAt).getTime() > now;
}

/**
 * The required skills this technician does NOT hold (an expired certification
 * counts as missing). Empty when the tech qualifies — including when nothing is
 * required.
 */
export function missingSkills(
  technician: Pick<SchedulerTechnician, 'expertise'>,
  required: readonly RequiredSkillRef[] | null | undefined,
  now: number = Date.now(),
): RequiredSkillRef[] {
  const needed = normalizeRequiredSkills(required);
  if (needed.length === 0) return [];
  const expertise = technician.expertise ?? [];
  return needed.filter((skill) => {
    const match = expertise.find((item) => {
      const serviceType = item.serviceType;
      const matchesIdentity =
        (skill.id && (item.serviceTypeId === skill.id || serviceType?.id === skill.id)) ||
        (skill.slug && serviceType?.slug === skill.slug);
      if (!matchesIdentity) return false;
      return expertiseIsCurrent(item.expiresAt, now);
    });
    return !match;
  });
}

/** A tech qualifies only when they hold EVERY required skill (current cert). */
export function technicianIsQualified(
  technician: Pick<SchedulerTechnician, 'expertise'>,
  required: readonly RequiredSkillRef[] | null | undefined,
  now?: number,
): boolean {
  return missingSkills(technician, required, now).length === 0;
}

export interface QualifiedTechnician<T extends Pick<SchedulerTechnician, 'expertise' | 'name'>> {
  technician: T;
  missing: RequiredSkillRef[];
}

/**
 * Order a picker's technicians: qualified first, then by name (stable within
 * each tier). Each entry carries what's missing so the row can render a suffix.
 */
export function sortQualifiedFirst<T extends Pick<SchedulerTechnician, 'expertise' | 'name'>>(
  technicians: readonly T[],
  required: readonly RequiredSkillRef[] | null | undefined,
  now?: number,
): QualifiedTechnician<T>[] {
  const rows = technicians.map((technician, index) => ({
    technician,
    missing: missingSkills(technician, required, now),
    index,
  }));
  rows.sort((a, b) => {
    const aQualified = a.missing.length === 0 ? 0 : 1;
    const bQualified = b.missing.length === 0 ? 0 : 1;
    if (aQualified !== bQualified) return aQualified - bQualified;
    const byName = a.technician.name.localeCompare(b.technician.name);
    if (byName !== 0) return byName;
    return a.index - b.index;
  });
  return rows.map(({ technician, missing }) => ({ technician, missing }));
}

function skillDisplayName(skill: RequiredSkillRef): string {
  return skill.name ?? skill.slug ?? skill.id ?? 'skill';
}

/** "Missing: Gas fitting" (one) / "Missing 2 skills" (several). Empty when nothing is missing. */
export function missingSkillSuffix(missing: readonly RequiredSkillRef[]): string {
  if (missing.length === 0) return '';
  if (missing.length === 1) return `Missing: ${skillDisplayName(missing[0]!)}`;
  return `Missing ${missing.length} skills`;
}

/** Omitted coverage data is unknown; an empty list is known not to cover the ZIP. */
export function missingCoverageSuffix(
  technician: Pick<SchedulerTechnician, 'serviceAreas'>,
  zipCode?: string | null,
): string {
  if (!zipCode || !technician.serviceAreas) return '';
  const codes = new Set(postalAreaLookupCodes(zipCode));
  if (!codes.size) return '';
  const covers = technician.serviceAreas.some((area) =>
    codes.has(normalizePostalAreaCode(area.zipCode ?? '')),
  );
  return covers ? '' : `Does not cover ZIP ${normalizePostalAreaCode(zipCode)}`;
}
